/* eslint-disable no-console */
import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormContextProvider } from '@stormwater-software/lib-form';
import {
  faEllipsisV,
  faExternalLinkAlt,
  faSort,
  faCalendarAlt,
  faList,
  faStickyNote,
  faTrash,
  faDownload,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { Link, useHistory } from 'react-router-dom';
import Loading from '../../../../components/Shared/ResourceIndex/Loading';
import CalendarView from './CalendarView';
import classNames from 'classnames';
import DashboardContext from '../../../../contexts/DashboardContext';
import EllipsisPopover from '../../shared/EllipsisPopover/EllipsisPopover';
import ProjectList from './ProjectList';
import userApi from '../../../../utils/api/user';
import AppContext from '../../../../contexts/AppContext';
import ExternalProjectsInspectionSubmitModal from '../../../Inspections/Listing/ExternalProjectsInspectionSubmitModal';
import xhrService from '../../../../utils/xhrService';
import authorizationAgreementApi from '../../../../utils/api/authorizationAgreement';
import inspectionApi from '../../../../utils/api/inspection';
import offlineApi from '../../../../utils/api/offline';
import mapApi from '../../../../utils/api/map';
import DirectionModal from './DirectionModal';
import ConfirmationModal from '../../../Shared/ConfirmationModal/ConfirmationModal';
import findingApi from '../../../../utils/api/finding';
import clientApi from '../../../../utils/api/client';
import { inspectionQuestionApi } from '../../../../utils/api/inspectionQuestion';
import { certificationApi } from '../../../../utils/api/certification';
import commentableApi from '../../../../utils/api/commentable';
import { getDownloadUrl } from '../../../../utils/api/upload';
import projectInspectionApi from '../../../../utils/api/projectInspection';
import { questionTypeApi } from '../../../../utils/api/questionType';
import { templateQuestionConfigApi } from '../../../../utils/api/templateQuestionConfig';
import { findingTypesApi } from '../../../../utils/api/findingTypes';
import { inspectionTemplateApi } from '../../../../utils/api/inspectionTemplate';
import { questionConfigValueApi } from '../../../../utils/api/questionConfigValue';
import { projectApi } from '../../../../utils/api/project';
import { uploadApi } from '../../../../utils/api/upload';
import { syncOfflineProject } from '../../../../utils/offlineProjectSync';
import { signatureEditorApi } from '../../../../utils/api/signatureEditor';
import { positionableApi } from '../../../../utils/api/positionable';
import { templatePriorityOptionsApi } from '../../../../utils/api/templatePriorityOptionsApi'
import { inspectionTemplateValidationApi } from '../../../../utils/api/inspectionTemplateValidation';
import db, { removeOfflineProjects as removeOfflineProject, downloadAndStoreImage, templateValidationKey, CLIENT_CUSTOM_FIELDS, putOfflineClientCustomFields, putOfflineTenantFeatures } from '../../../../db/db';
import tenantApi from '../../../../utils/api/tenant';
import legendItemApi from '../../../../utils/api/legendItem';
import { uniq } from 'lodash';
import { LinearProgress, Typography, Paper } from '@mui/material';
import { toast } from 'react-toastify';
import { useOffline } from '../../../../contexts/OfflineContext'
import { AppDivisionContext } from "../../../../contexts/AppDivisionContext";
import permitApi from '../../../../utils/api/permit';
import TenantFeatureContext from '../../../../contexts/TenantContext';
import { isValidLatLong } from '../../../../utils/googlemaps';
import { format } from 'date-fns';
import schedulerApi from '../../../../utils/api/inspectionSchedular';
import { useTenantRedirect } from '../../../../hooks/useTenantRedirect';
import MFAVerificationModal from '../../../Shared/MFAVerificationModal';
import {
  CANONICAL_LEGEND_ITEM_TYPE,
  toCanonicalPositionableType,
} from '../../../MapEditor/Positionable/legendItemType';

function getCtaRoute(inspectionStatus, dueDate, projectId, inspectionId = null) {
  if (
    inspectionStatus === 'scheduled' &&
    moment().format('MM-DD-YYYY') === dueDate &&
    navigator.onLine
  ) {
    return `/projects/${projectId}/start-inspection`;
  }

  if (inspectionId > 0 && dueDate) {
    const today = new Date();
    const due = parseDueDateLocal(dueDate);

    today.setHours(0, 0, 0, 0);
    if (today > due) {
      return `/inspection/${inspectionId}/questionnaire`;
    }
  }

  return `/projects/${projectId}/inspections`;
}

const fetchInspectionTemplateData = async (templateId) => {

  const [findingTypes, templateQuestionConfig, priority] =
    await Promise.all([
      findingTypesApi.index(Number(templateId)),
      templateQuestionConfigApi.index(templateId),
      templatePriorityOptionsApi.getByTemplateId(templateId),
    ]);

  await db.findingTypeInspectionTemplate
    .put({
      // Coerced: IndexedDB keys are type-sensitive ("42" is not 42) and every
      // reader looks this up with Number(). An id arriving as a string here
      // would make getOfflineFindingTypes miss silently.
      templateId: Number(templateId),
      findingTypes: findingTypes,
    })
    .catch((err) => {
      console.error('Error storing finding types:', err);
    });

  // templateQuestionConfig and priority were already being fetched here and
  // thrown away, which left the inspection form's and finding modal's dropdowns
  // with no options offline.
  await Promise.all([
    db.templateQuestionConfigs
      .put({ template_id: Number(templateId), data: templateQuestionConfig })
      .catch((err) => {
        console.error('Error storing template question config:', err);
      }),
    db.templatePriorityOptions
      .put({ template_id: Number(templateId), data: priority })
      .catch((err) => {
        console.error('Error storing template priority options:', err);
      }),
  ]);
}

const downloadInspectionTemplateData = async (templateId) => {

  const questionsData = await inspectionQuestionApi.getDefaultAnswers(
    templateId,
  );

  // Process condition questions
  if (questionsData.question_groups) {
    const conditionGroup = questionsData.question_groups.find(
      (group) => group.name === 'Condition Group',
    );

    if (conditionGroup) {
      const conditionQuestions = conditionGroup.questions.filter(
        (question) => question.input_label === 'Condition',
      );

      for (const question of conditionQuestions) {
        // Result was previously discarded — these are the select-one /
        // select-many answer options the form needs offline.
        const configValues = await questionConfigValueApi
          .index(question.id)
          .catch((err) => {
            console.warn('Error fetching question config values:', err);

            return null;
          });

        if (configValues) {
          await db.questionConfigValues
            .put({ question_id: Number(question.id), data: configValues })
            .catch((err) => {
              console.error('Error storing question config values:', err);
            });
        }
      }
    }
  }

  return questionsData.question_groups;
};

// Parses MM-DD-YYYY to local midnight, avoiding UTC offset shifting the date in US timezones
function parseDueDateLocal(dueDate) {
  const [month, day, year] = dueDate.split('-');

  return new Date(Number(year), Number(month) - 1, Number(day));
}

function getCtaText(dueDate, inspectionStatus) {
  if (!dueDate) {
    return 'View Project';
  }

  const formattedTodayDate = new Date();
  const formattedDueDate = parseDueDateLocal(dueDate);

  if (
    formattedTodayDate.setHours(0, 0, 0, 0) === formattedDueDate.getTime() &&
    (inspectionStatus === 'scheduled' || inspectionStatus === 'new')
  ) {
    return 'Start Inspection';
  }

  if (
    formattedTodayDate.setHours(0, 0, 0, 0) === formattedDueDate.getTime() &&
    (inspectionStatus === 'started' || inspectionStatus === 'answered')
  ) {
    return 'Finish';
  }

  if (formattedTodayDate > formattedDueDate) {
    return 'Past Due';
  }

  return 'View Project';
}

/* "My Projects" widget was formerly titled as "Inspections Due".
 * In case, looking for naming convention to make sense,
 * think of "Inspections Due" as the main widget with "My Project/Project List" as a part of it.
 */
const InspectionsDue = ({ providedProps, changeShowMore, mdSize }) => {
  const history = useHistory();
  const [search, setSearch] = useState('');
  const [clientIsAscending, setClientIsAscending] = useState(true);
  const [siteIsAscending, setSiteIsAscending] = useState(true);
  const [startingInspectionKey, setStartingInspectionKey] = useState(null);
  const {
    displayStatsForID,
    isLoadingForID,
    allProjectsForID,
    setDisplayStatsForID,
    allProjectsForMP,
    setDisplayStatsForMP,
    fetchStatsFuncForID,
    getTeamManagementData,
  } = useContext(DashboardContext);
  const widgetRef = useRef(0);
  const [widgetWidth, setWidgetWidth] = useState(widgetRef.current.clientWidth);
  const appContext = useContext(AppContext);
  const [
    showExternalProjectInspectionModal,
    setShowExternalProjectInspectionModal,
  ] = useState(false);
  const [modalInspection, setModalInspection] = useState({});
  const [inspectionTypes, setInspectionTypes] = useState({});
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [openDirectionModal, setOpenDirectionModal] = useState(false);
  const [showRemoveOfflineDownloadModal, setShowRemoveOfflineDownloadModal] = useState(false);
  const [offlineProjectId, setOfflineProjectId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [offlineProjects, setOfflineProjects] = useState([]);
  const steps = [
    'Downloading project...',
    'Downloading inspection...',
    'Downloading images...',
    'Downloading assets...',
    'Downloading data...',
    'Processing files...',
    'Verifying integrity...',
    'Finalizing setup...',
    'Completing download...',
    'Download complete!',
  ];
  const [statusText, setStatusText] = useState('Initializing...');

  const [syncProgress, setSyncProgress] = useState(0);
  const [syncStatus, setSyncStatus] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const isOffline = useOffline();
  const appDivisionContext = useContext(AppDivisionContext);
  const tenantFeatureContext = useContext(TenantFeatureContext);
  const {
    redirectToTenant,
    showMFAModal,
    mfaData,
    handleMFAVerificationSuccess,
    handleMFAModalClose,
    authStore,
  } = useTenantRedirect();

  const user = appContext.get('user');

  let viewName = 'Inspections Due';

  let viewIcon = faCalendarAlt;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  if (localStorage.getItem('myProjectsWidgetPreference') === 'InspectionsDue') {
    viewIcon = faCalendarAlt;
    viewName = 'Insp. Calendar';
  }

  if (localStorage.getItem('myProjectsWidgetPreference') === 'Insp. Calendar') {
    viewIcon = faStickyNote;
    viewName = 'Inspections Due';
  }

  useEffect(() => {
    function handleResize() {
      setWidgetWidth(widgetRef.current.clientWidth);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeView = () => {
    setSelectedProjects([]);
    if (
      localStorage.getItem('myProjectsWidgetPreference') === 'InspectionsDue'
    ) {
      localStorage.setItem('myProjectsWidgetPreference', 'Insp. Calendar');
    } else {
      localStorage.setItem('myProjectsWidgetPreference', 'InspectionsDue');
    }
    setAnchorEl(null);
  };

  const handleProjectListView = () => {
    setSelectedProjects([]);
    localStorage.setItem('myProjectsWidgetPreference', 'ProjectList');
    setAnchorEl(null);
  };

  const handleButtonColors = (dueDate, inspectionStatus) => {
    if (getCtaText(dueDate, inspectionStatus) === 'View Project')
      return 'list-view-show-project-color';

    if (getCtaText(dueDate, inspectionStatus) === 'Past Due')
      return 'list-view-past-due-color';

    if (getCtaText(dueDate, inspectionStatus) === 'Finish')
      return 'list-view-finish-project-color';

    return 'list-view-start-inspection-color';
  };

  // Offline a full page load cannot fetch the document, so route client-side.
  // Online we keep the hard navigation, which resets component state as before.
  const goToInspection = (inspectionId) => {
    const path = `/inspection/${inspectionId}/questionnaire`;

    if (!navigator.onLine) {
      history.push(path);

      return;
    }

    window.location.href = path;
  };

  const handleStartInspection = async (ele) => {
    try {
      if (!ele.projectId) {
        console.error('Project ID is required to start inspection');
        toast.error('Project ID is missing');

        return;
      }

      // Downloading a project creates the inspection that is due, but the
      // dashboard row still carries no inspectionId, so offline this fell
      // through to creating a second one. Open the downloaded inspection
      // instead. Only an unfinished inspection is a candidate — a completed one
      // should not be reopened by "Start Inspection".
      if (!navigator.onLine) {
        const existing = await db.inspections
          .where('project_id')
          .equals(Number(ele.projectId))
          .filter((inspection) => inspection.status !== 'complete')
          .reverse()
          .sortBy('inspectionNumber')
          .catch((err) => {
            console.error('Error looking up offline inspection:', err);

            return [];
          });

        if (existing && existing.length && existing[0].id) {
          if (ele.division?.id) {
            appDivisionContext.saveAppDivisionId(ele.division.id);
          }

          goToInspection(existing[0].id);

          return;
        }

        toast.error(
          'This inspection has not been downloaded. Reconnect to start it.',
        );

        return;
      }

      // Use project_inspection_template_id as the junction ID
      const junctionId = ele.project_inspection_template_id || ele.junctionId;

      if (!junctionId) {
        console.error('Junction ID (project_inspection_template_id) is required to start inspection');
        toast.error('Junction ID is missing');

        return;
      }

      // Map inspection type to ensure it matches API expectations
      const validInspectionTypes = [
        'Routine',
        'Routine + Post Storm',
        'Post Storm',
        'Final',
        'Other',
        'Pre-Qualifying Precipitation Event',
        'During Qualifying Precipitation Event',
        'Post-Qualifying Precipitation Event',
        'Before Release of Contained Storm Water',
        'During Release of Contained Storm Water',
        'Quarterly Non-Storm Water'
      ];

      let inspectionType = ele.inspection_type || ele.inspectionType || 'Routine';

      // If the inspection type is not in the valid list, default to 'Routine'
      if (!validInspectionTypes.includes(inspectionType)) {
        console.warn(`Invalid inspection type "${inspectionType}", defaulting to "Routine"`);
        inspectionType = 'Routine';
      }

      const requestBody = {
        junctionId: junctionId,
        projectId: ele.projectId,
        inspection_types: [inspectionType]
      };

      // Use the inspection API with the createWithJunction method
      const data = await inspectionApi.createWithJunction(requestBody);

      if (data && data.id) {
        // Navigate to the newly created inspection's questionnaire
        window.location.href = `/inspection/${data.id}/questionnaire`;

        // Optionally refetch data
        getTeamManagementData.refetch();
        fetchStatsFuncForID.refetch();
      } else {
        console.error('Failed to create inspection: No inspection ID returned');
        toast.error('Failed to start inspection. Please try again.');
      }
    } catch (error) {
      console.error('Error starting inspection:', error);
      if (error?.response?.status === 403 && error?.response?.data?.error?.includes('Authorization & Agreement')) {
        toast.error('The Authorization & Agreement must be signed by both parties before inspections can be started.');
      } else {
        toast.error('Failed to start inspection. Please try again.');
      }
    }
  };

  const handleCalendarStartInspection = async (ele) => {
    const { projectId, dueDate, division } = ele;

    try {
      appDivisionContext.saveAppDivisionId(division.id);

      const response = await schedulerApi.getUpcomingInspections(projectId);

      if (!response.success || !response.data) {
        toast.error('Failed to fetch inspection details. Please try again.');

        return;
      }

      // dueDate is MM-DD-YYYY — convert to local midnight for comparison
      const [month, day, year] = dueDate.split('-');
      const dueMidnight = new Date(Number(year), Number(month) - 1, Number(day));

      dueMidnight.setHours(0, 0, 0, 0);

      const isDateMatch = (ins) => {
        const d = new Date(ins.date_of_inspection);
        const insMidnight = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

        insMidnight.setHours(0, 0, 0, 0);

        return (
          insMidnight.getTime() === dueMidnight.getTime() &&
          ins.status !== 'complete' &&
          ins.status !== 'cancelled'
        );
      };

      // Prefer scheduled_inspection records — their id is a project_inspection_templates.id (valid junctionId).
      // Existing-inspection records (is_existing && !scheduled_inspection) carry an inspections.id which is not a valid junctionId.
      const todayInspection =
        response.data.find((ins) => isDateMatch(ins) && ins.scheduled_inspection) ||
        response.data.find((ins) => isDateMatch(ins));

      if (!todayInspection || !todayInspection.id) {
        toast.error('No scheduled inspection found for today. Please refresh and try again.');

        return;
      }

      const validInspectionTypes = [
        'Routine', 'Routine + Post Storm', 'Post Storm', 'Final', 'Other',
        'Pre-Qualifying Precipitation Event', 'During Qualifying Precipitation Event',
        'Post-Qualifying Precipitation Event', 'Before Release of Contained Storm Water',
        'During Release of Contained Storm Water', 'Quarterly Non-Storm Water',
      ];

      let inspectionType = ele.inspection_type || ele.inspectionType || todayInspection.inspection_type || 'Routine';

      if (!validInspectionTypes.includes(inspectionType)) {
        inspectionType = 'Routine';
      }

      const requestBody = {
        junctionId: todayInspection.id,
        projectId,
        inspection_types: [inspectionType],
      };

      const data = await inspectionApi.createWithJunction(requestBody);

      if (data && data.id) {
        getTeamManagementData.refetch();
        fetchStatsFuncForID.refetch();
        window.location.href = `/inspection/${data.id}/questionnaire`;
      } else {
        toast.error('Failed to start inspection. Please try again.');
      }
    } catch (error) {
      console.error('Error starting inspection from calendar:', error);
      toast.error('Failed to start inspection. Please try again.');
    }
  };

  const handleEPVInspShowModal = (ele, startEvpInspection = false) => {
    if (startEvpInspection) {
      inspectionApi.create(ele.projectId, 'Routine', 'External Project View').then((i) => {
        const modalData = {
          inspectionId: i.id,
          siteName: i.project.name,
          ...i,
        };

        getTeamManagementData.refetch();
        fetchStatsFuncForID.refetch();
        setModalInspection(modalData);
        setShowExternalProjectInspectionModal(true);
      });
    } else {
      setModalInspection(ele);
      setShowExternalProjectInspectionModal(true);
    }
  };

  const handleEPVInspSubmit = async (formData) => {
    appContext.loadData(
      async () =>
        await xhrService
          .put(
            `/api/inspections/${modalInspection.inspectionId}/update-evp-inspection`,
            {
              ...formData,
            },
          )
          .then(() => {
            setShowExternalProjectInspectionModal(false);
            setModalInspection({});
            fetchStatsFuncForID.refetch();
          }),
    );
  };

  const getInspectionTypes = async () => {
    const dataTypes = await xhrService.get(`/api/inspection-types`);
    const mappedData = dataTypes.data.map((e) => ({ type: e }));

    setInspectionTypes(mappedData);
  };

  //SEARCH
  useEffect(() => {
    setSelectedProjects([]);
    if (
      localStorage.getItem('myProjectsWidgetPreference') === 'InspectionsDue' ||
      localStorage.getItem('myProjectsWidgetPreference') === 'Insp. Calendar'
    ) {
      const updatedList =
        allProjectsForID &&
        [...allProjectsForID].filter((el) => {
          return el.siteName.toLowerCase().includes(search.toLowerCase());
        });

      setDisplayStatsForID(updatedList);
    } else {
      const updatedList = [...allProjectsForMP].filter((el) => {
        return (
          el.project.name.toLowerCase().includes(search.toLowerCase()) ||
          el.project.client.name.toLowerCase().includes(search.toLowerCase())
        );
      });

      setDisplayStatsForMP(updatedList);
    }
  }, [search]);


  const setOfflineProjectsFunc = async () => {

    const projectsDownloadedByUser = await offlineApi.getOfflineByUser(user.id)

    const offlineProjectsData = await db.projects.toArray();

    const offlineProject = offlineProjectsData.filter((project) => {
      return projectsDownloadedByUser.projects.some(
        (offlineProject) => offlineProject.project_id === project.id,
      );
    });

    const offlineProjectDeleted = offlineProjectsData.filter((project => {
      return !projectsDownloadedByUser.projects.some(
        (offlineProject) => offlineProject.project_id === project.id,
      );
    }));

    for (const project of offlineProjectDeleted) {
      await removeOfflineProject(project.id);
    }


    if (offlineProject.length === 0) {
      setOfflineProjects([]);
      if (isOffline)
        setDisplayStatsForID([])

      return;
    }

    setOfflineProjects(offlineProject);
  };


  useEffect(() => {

    if (isOffline && displayStatsForID && displayStatsForID.length && offlineProjects.length) {
      setDisplayStatsForID((prev) => {
        return prev.filter((item) => {
          return offlineProjects.some((offlineProj) => offlineProj.id === item.projectId);
        });
      })
    }

  }, [isOffline, offlineProjects])

  useEffect(() => {

    setOfflineProjectsFunc();

    getInspectionTypes();
    if (localStorage.getItem('myProjectsWidgetPreference') === null) {
      localStorage.setItem('myProjectsWidgetPreference', 'InspectionsDue');
    }
  }, []);

  //SORT
  const sortBySiteName = () => {
    const updatedList = displayStatsForID.sort((a, b) => {
      let A = a.siteName.toString().toLowerCase();

      let B = b.siteName.toString().toLowerCase();

      if (siteIsAscending === true) {
        setSiteIsAscending(!siteIsAscending);

        return A > B ? -1 : 1; // descending
      } else {
        setSiteIsAscending(!siteIsAscending);

        return A > B ? 1 : -1; //ascending
      }
    });

    setDisplayStatsForID(updatedList);
  };

  const sortByClientName = () => {
    const updatedList = displayStatsForID.sort((a, b) => {
      let A = a.clientName.toString().toLowerCase();

      let B = b.clientName.toString().toLowerCase();

      if (clientIsAscending === true) {
        setClientIsAscending(!clientIsAscending);

        return A > B ? -1 : 1; // descending
      } else {
        setClientIsAscending(!clientIsAscending);

        return A > B ? 1 : -1; //ascending
      }
    });

    setDisplayStatsForID(updatedList);
  };

  const sortByDueDate = () => {
    const updatedList = displayStatsForID.sort((a, b) => {
      let A = a.dueDate ? parseDueDateLocal(a.dueDate) : new Date(0);

      let B = b.dueDate ? parseDueDateLocal(b.dueDate) : new Date(0);

      if (clientIsAscending === true) {
        setClientIsAscending(!clientIsAscending);

        return A > B ? -1 : 1; // descending
      } else {
        setClientIsAscending(!clientIsAscending);

        return A > B ? 1 : -1; //ascending
      }
    });

    setDisplayStatsForID(updatedList);
  };

  let oneColumnHeader = {
    // gridTemplateAreas: '"title title title . bars bars bars bars bars bars bars ellipsis"'
  };

  const handleRedirect = (ele) => {
    const redirectPath = `/divisions/${ele.division.id}${getCtaRoute(
      ele.inspectionStatus,
      ele.dueDate,
      ele.projectId,
    )}`;

    redirectToTenant(ele.division.tenant_id, redirectPath);
  };

  // Returns true when the project either does not require the Authorization & Agreement
  // or has an executed agreement; otherwise redirects the user to the project inspections
  // page and returns false. Failures fall open so transient API issues do not block start.
  const checkAgreementBeforeStart = async (ele) => {
    // This endpoint is unreachable offline and the catch below already treats a
    // failure as "allow", so skip the request rather than issue one that can
    // only fail. Behaviour is unchanged; it just stops the console noise.
    if (!navigator.onLine) {
      return true;
    }

    try {
      const agreementData = await authorizationAgreementApi.getAgreement(ele.projectId);

      if (agreementData.required && (!agreementData.agreement || agreementData.agreement.status !== 'executed')) {
        toast.error('An Authorization & Agreement must be signed before starting this inspection. Please visit the project inspections page.');
        appDivisionContext.saveAppDivisionId(ele.division.id);
        redirectToTenant(
          ele.division.tenant_id,
          `/divisions/${ele.division.id}/projects/${ele.projectId}/inspections`,
        );

        return false;
      }
    } catch (err) {
      // Allow navigation on API error
    }

    return true;
  };

  const handleCheckboxChange = (project) => {
    setSelectedProjects((prevSelectedProjects) =>
      prevSelectedProjects.find(
        (selectedProject) => selectedProject.id === project.id,
      )
        ? prevSelectedProjects.filter(
          (selectedProject) => selectedProject.id !== project.id,
        )
        : [...prevSelectedProjects, project],
    );
  };

  const GetDirection = async (direction) => {

    const latLongError = []
    const addresses = selectedProjects.map((project) => {
      if (!isValidLatLong(project.address ? project.address : project)) {
        latLongError.push(project.name ? project.name : project.id);
      }

      return project.address ? project.address : project;
    });


    if (latLongError.length > 0) {
      toast.error(
        <>
          The following projects are missing valid latitude and longitude data, which is required to complete routing.
          <br />
          {latLongError.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </>
      );

      return;
    }


    if (!addresses.length) {
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      const startLat = position.coords.latitude;
      const startLong = position.coords.longitude;

      const params = {
        origin: `${startLat},${startLong}`,
        waypoints: addresses
          .map((add) => `${add.latitude},${add.longitude}`)
          .join('|'),
      };

      if (direction === 3) {

        mapApi.getOptimizedRoute(params).then((response) => {

          setOpenDirectionModal(false);

          const optimisedWaypoints = response.data.optimizedOrder.map((index) => addresses[index]);


          const optimizedWaypointString = optimisedWaypoints
            .map((loc) => `${loc.latitude},${loc.longitude}`)
            .join('|');


          const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${startLat},${startLong}&destination=${startLat},${startLong}&waypoints=${optimizedWaypointString}`;

          const popup = window.open(googleMapsUrl, '_blank');

          if (!popup) {
            alert(
              'Pop-up blocked! Redirecting to Google Maps in the same tab.',
            );
            window.location.href = googleMapsUrl;
          }


        }).catch((err => {
          console.error('Error fetching optimized route:', err);
          toast.error('Error fetching optimized route:', err);

        })
        )
      }

      else {
        addresses.unshift({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });

        params.waypoints = addresses
          .map((add) => `${add.latitude},${add.longitude}`)
          .join('|');

        mapApi.directions(params).then((response) => {
          if (response) {
            const optimizedWaypoints = response.data;

            setOpenDirectionModal(false);

            if (optimizedWaypoints.length === 1) {
              alert('No route found');

              return;
            }

            let optimizedLocations = optimizedWaypoints
              .map((index) => addresses[index])
              .slice(1);

            if (direction === 2) {
              optimizedLocations = optimizedLocations.reverse();
            }

            const optimizedWaypointString = optimizedLocations
              .slice(0, optimizedLocations.length - 1)
              .map((loc) => `${loc.latitude},${loc.longitude}`)
              .join('|');

            const destAddress1 =
              optimizedLocations[optimizedLocations.length - 1];
            const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${startLat},${startLong}&destination=${destAddress1.latitude},${destAddress1.longitude}&waypoints=${optimizedWaypointString}`;

            const popup = window.open(googleMapsUrl, '_blank');

            if (!popup) {
              alert(
                'Pop-up blocked! Redirecting to Google Maps in the same tab.',
              );
              window.location.href = googleMapsUrl; // Fallback to same tab if pop-up is blocked
            }
          }
        });
      }
    } catch (err) {
      if (err.code === err.PERMISSION_DENIED) {
        alert(
          'You have denied location access. Please enable location services to proceed.',
        );
        alert(
          'To enable location, go to your browser settings or device settings.',
        );
      } else if (err.code === err.POSITION_UNAVAILABLE) {
        alert(
          "Location information is unavailable. Please check your device's connection.",
        );
      } else if (err.code === err.TIMEOUT) {
        alert('Location request timed out. Please try again.');
      } else {
        alert('An unknown error occurred while requesting location.');
      }
    }
  };

  const onDownload = async (
    projectId,
    inspectionTemplateId,
    allProjects,
    junctionId,
  ) => {
    try {
      setDownloading(true);
      setProgress(0);
      setStatusText('Initializing download...');

      // Scoped to the row that was actually clicked, by junction id. This used to
      // match on projectId alone, so on a project with several assigned forms any
      // form due today satisfied it — and the project-level create it then called
      // resolved the template itself, landing the inspection on the project's
      // PRIMARY form and advancing that form's next_inspection_date rather than
      // the clicked one's.
      const dueRow = junctionId
        ? (allProjects || []).find(
            (item) =>
              item.projectId === projectId &&
              (item.project_inspection_template_id || item.junctionId) ===
                junctionId &&
              item.inspectionStatus === 'scheduled' &&
              format(new Date(), 'MM-dd-yyyy') === item.dueDate,
          ) || null
        : null;

      // Calculate total steps for better progress tracking
      const totalSteps = 15;

      let completedSteps = 0;

      const updateProgress = (stepIncrement = 1, message = null) => {
        completedSteps += stepIncrement;

        const newProgress = Math.min(
          Math.floor((completedSteps / totalSteps) * 100),
          95,
        );

        setProgress(newProgress);
        if (message) setStatusText(message);
      };

      // Step 1: Download project data
      setStatusText(steps[0] || 'Downloading project...');
      updateProgress(1);

      const response = await offlineApi.download(projectId, inspectionTemplateId).catch((error) => {
        console.error('Error downloading project:', error);
        throw new Error(error.message);
      });

      // Started here but awaited much later, just before the inspection fetches
      // that actually need it. It used to be awaited up front, which put its full
      // duration — 22s on a recent capture — ahead of every other request in the
      // download even though nothing until projectInspectionApi.index reads the
      // result. The catch is attached now rather than at the await, so a failure
      // while the project data downloads cannot surface as an unhandled rejection.
      let inspectionCreation = Promise.resolve(null);

      if (!junctionId) {
        // Deliberately no fallback to the project-level create: without a junction
        // id that path resolves the primary form, which is the bug this replaced.
        // Creating nothing is recoverable; creating on the wrong form shifts a
        // schedule silently.
        console.error(
          `Skipping the inspection pre-create for project ${projectId}: the widget row carries neither project_inspection_template_id nor junctionId, so there is no way to tell which assigned form was clicked.`,
        );
      } else if (dueRow) {
        inspectionCreation = inspectionApi
          .createWithJunction({
            junctionId,
            projectId,
            inspection_types: ['Routine'],
          })
          .catch((error) => {
            console.error('Error creating inspection:', error);

            return null;
          });
      }

      updateProgress(2, steps[1] || 'Processing projects...');

      // Step 2: Process each project in sequence to avoid overwhelming the system
      for (const project of response.projects) {
        try {
          setStatusText(`Processing project ${project.name}...`);

          // Fetch client data. Request every custom field any offline consumer
          // asks for — the finding form needs more of them than this download
          // originally fetched.
          // tenant features and the site-map doc types used to be awaited one after
          // another further down; nothing here depends on the others, so they join
          // the same batch.
          const [client, customFields, projectDocs, , tenantFeatures, uploads] =
            await Promise.all([
              clientApi.show(project.client_id),
              clientApi.getCustomFields(project.client_id, CLIENT_CUSTOM_FIELDS),
              projectApi.getDocs(project.id),
              permitApi.getProjectPermitStatus(project.id),
              // Feature-gated controls (e.g. the finding form's Legend Item select)
              // check tenantHasFeature, which has no offline source of its own.
              tenantApi.features.getTenantFeature().catch((err) => {
                console.warn('Error fetching tenant features:', err);

                return null;
              }),
              uploadApi.docTypes('Active Site Maps', project.id),
            ]);

          // The map editor gates rendering on the client record.
          if (client?.id) {
            await db.clients.put(client).catch((err) => {
              console.error('Error storing client:', err);
            });
          }

          if (customFields) {
            await putOfflineClientCustomFields(
              project.client_id,
              customFields,
            ).catch((err) => {
              console.error('Error storing client custom fields:', err);
            });
          }

          if (tenantFeatures) {
            await putOfflineTenantFeatures(tenantFeatures).catch((err) => {
              console.error('Error storing tenant features:', err);
            });
          }

          // Cache only the 'Active Site Maps' document type. Offline consumers
          // (SiteMaps, the map editor toolbar) read only this one type, while the
          // full payload grows with every upload the project has ever had.
          await db.siteMapUploads
            .put({
              project_id: project.id,
              doc_type_ids: [],
              data: [],
              docs: {
                documentTypes: (projectDocs?.documentTypes || []).filter(
                  (dt) => dt.name === 'Active Site Maps',
                ),
              },
            })
            .catch((err) => {
              console.error('Error storing project docs:', err);
            });

          updateProgress(1);

          const downloadPromises = [];

          if (uploads.dt && uploads.dt.length > 0) {
            const docTypeIds = uploads.dt.map((dt) => dt.id);
            const res = await uploadApi.project.index(project.id, docTypeIds);

            // Persist the upload records themselves, not just their image bytes.
            // The map editor resolves a map by matching upload.GUID, and that
            // metadata was previously only reachable over the network — which is
            // why a downloaded map could not be opened offline.
            // Read-modify-put rather than a bare update: `update` silently does
            // nothing when the row is absent, so if the put above failed (it only
            // logs) the site-map records would be dropped with no diagnostic.
            // Merging preserves the cached project docs that put wrote.
            await db.siteMapUploads
              .get(Number(project.id))
              .then((existing) =>
                db.siteMapUploads.put({
                  ...(existing || {}),
                  project_id: Number(project.id),
                  doc_type_ids: docTypeIds,
                  data: res,
                }),
              )
              .catch((err) => {
                console.error('Error storing site map uploads:', err);
              });

            // One validation request per site map, concurrently rather than one
            // after another — each is independent of the others.
            await Promise.all(
              res.map(async (upload) => {
                // Cache the validation result — the map editor derives its doc
                // type from it, and offline it has no other source.
                const validation = await inspectionTemplateValidationApi
                  .validate(project.id, upload.upload.GUID)
                  .catch((err) => {
                    console.warn('Error validating inspection template:', err);

                    return null;
                  });

                if (validation) {
                  await db.templateValidations
                    .put({
                      key: templateValidationKey(project.id, upload.upload.GUID),
                      project_id: project.id,
                      upload_guid: upload.upload.GUID,
                      data: validation,
                    })
                    .catch((err) => {
                      console.error('Error storing template validation:', err);
                    });
                }
              }),
            );

            for (const upload of res) {
              downloadPromises.push(
                downloadAndStoreImage(
                  getDownloadUrl(
                    upload.upload.GUID,
                    null,
                    false,
                    Date.now(),
                    null,
                    null,
                    false,
                  ),
                  upload.upload.GUID,
                  upload.upload.name,
                  upload.upload.mime_type,
                  project.id,
                ),
              );
            }
          }

          // Wait for all image downloads to complete
          await Promise.allSettled(downloadPromises);
          updateProgress(1, steps[2] || 'Downloading images...');

          // Download and store legend items for the project's regulation group
          try {
            setStatusText(`Downloading legend items for project ${project.name}...`);

            // Fetch legend items by project - this gets both global and project-specific legend items
            const legendItems = await legendItemApi.indexByProject(
              project.id,
              appDivisionContext.appDivisionId
            ).catch((err) => {
              console.warn('Error fetching legend items:', err);

              return [];
            });

            // Store legend items in IndexedDB. One bulkPut rather than a put per
            // item: a project can carry hundreds, and each await was its own
            // IndexedDB round-trip.
            await db.legendItems
              .bulkPut(
                legendItems.map((legendItem) => ({
                  ...legendItem,
                  project_id: project.id,
                  regulation_group_id: project.document_group,
                  sync: { synced: true, payload: [] },
                })),
              )
              .catch((err) => {
                console.error('Error storing legend items:', err);
              });

            updateProgress(1, 'Legend items downloaded...');
          } catch (legendError) {
            console.error('Error downloading legend items:', legendError);
            // Continue even if legend items fail
          }

          // The "map data" block that used to sit here re-fetched
          // uploadApi.project.index once per doc type and did nothing with the
          // result — its only consumer was commented out, leaving a console.log.
          // The same records are already fetched above in one call, for every doc
          // type at once, and stored in db.siteMapUploads. Two round-trips per
          // download removed; the progress step is kept so the bar is unchanged.
          updateProgress(1, 'Map data downloaded...');

          // Store project data
          await db.projects
            .put({ ...project, downloadedOn: new Date().toISOString() })
            .catch((err) => {
              console.error('Error storing project:', err);
            });

          updateProgress(1);
        } catch (projectError) {
          console.error(
            `Error processing project ${project.id}:`,
            projectError,
          );
          // Continue with next project instead of failing the entire process
        }

        // Step 3: Download inpspection template questions and signature

        // Five independent requests that used to run strictly one after another.
        const [inspectionTemplate, , questionGroups, signature, questionTypes] =
          await Promise.all([
            inspectionTemplateApi.get(inspectionTemplateId),
            fetchInspectionTemplateData(inspectionTemplateId),
            downloadInspectionTemplateData(inspectionTemplateId),
            signatureEditorApi.index(inspectionTemplateId),
            questionTypeApi.index().catch((err) => {
              console.warn('Error fetching question types:', err);
            }),
          ]);

        await db.inspectionTemplate.put({
          // Coerced for the same reason as findingTypeInspectionTemplate above —
          // readers use Number(), and a string key would miss silently.
          template_id: Number(inspectionTemplateId),
          questionTypes: questionTypes,
          template: {
            ...inspectionTemplate,
            question_groups: questionGroups,
          },
          signature: {
            attestations: signature.attestations,
            signatureGroups: signature.lines,
          },
        });
      }

      updateProgress(1, steps[3] || 'Processing inspections...');

      // Join the creation started at the top: the fetch below must see it.
      await inspectionCreation;

      // Step 4: Process inspections for each project
      // for (const projectId of projectId) {
      try {
        const inspectionResponse = await projectInspectionApi.index(
          projectId,
          1,
          1,
        );

        if (
          !inspectionResponse.pageData ||
          inspectionResponse.pageData.length === 0
        ) {
          return;
        }

        for (const inspection of inspectionResponse.pageData) {
          try {
            // Store inspection data, preserving a local row that has not synced.
            // Overwriting it with `synced: true` would drop the queued payload
            // the sync pass reads, so the inspector's offline edits would never
            // be uploaded.
            const existingInspection = await db.inspections
              .get(Number(inspection.id))
              .catch(() => null);

            if (existingInspection?.sync?.synced === false) {
              console.warn(
                `Kept local unsynced inspection ${inspection.id}; skipping overwrite.`,
              );
            } else {
              await db.inspections
                .put({ ...inspection, sync: { synced: true } })
                .catch((err) => {
                  console.error('Error storing inspection:', err);
                });
            }

            // Fetch project inspection data
            const uploads = await uploadApi.projectInspection.index(
              projectId,
              inspection.id,
            );

            await db.projectInspectionUploads
              .put({
                inspection_id: inspection.id,
                project_id: projectId,
                data: uploads,
                sync: { synced: true },
              })
              .catch((err) => {
                console.error(
                  'Error storing project inspection uploads:',
                  err,
                );
              });

            // Per upload these three are independent of one another, and the
            // uploads are independent of each other — previously all 3xN ran
            // strictly in sequence.
            await Promise.all(uploads.map(async (upload) => {
              const [data, data2, data3] = await Promise.all([
                positionableApi.index(
                  'project_inspection_uploads',
                  upload.id,
                  inspection.id,
                  'findings',
                ),
                positionableApi.index(
                  'project_uploads',
                  upload.project_upload_id,
                  inspection.id,
                  'legend_items',
                  'control_measure'
                ),
                positionableApi.index(
                  'project_uploads',
                  upload.project_upload_id,
                  inspection.id,
                  'legend_items',
                  'pollutant'
                ),
              ]);

              // SW2-2230: the offline legend-placement picker filters on `positionable_type`, so
              // every row must be written under one spelling. `??` was not enough — it only fires
              // when the field is absent, and a response or a cached row carrying a map-editor
              // subtype ("control_measure" / "pollutant") kept its own value and fell out of the
              // picker. Normalise unconditionally instead. See legendItemType.js.
              await db.positionables.bulkPut([
                ...data.map((item) => ({
                  ...item,
                  positionable_type: item.positionable_type ?? 'findings',
                  project_id: projectId,
                  sync: { synced: true, payload: [] },
                })),
                ...[...data2, ...data3].map((item) => ({
                  ...item,
                  positionable_type: toCanonicalPositionableType(
                    item.positionable_type ?? CANONICAL_LEGEND_ITEM_TYPE,
                  ),
                  project_id: projectId,
                  sync: { synced: true, payload: [] },
                })),
              ]);
            }));
            // Seven independent reads for this inspection, previously issued one
            // at a time down the length of this block. They are still consumed in
            // the same order below, so every write ordering and unsynced-work
            // guard is unchanged.
            const [
              ,
              inpsectionUploads,
              findings,
              questionData,
              certifications,
              ,
              notes,
            ] = await Promise.all([
              inspectionApi.getTenantBackdatingDetails(),
              inspectionApi.getInspectionUploads(inspection.id),
              findingApi.index(inspection.id),
              inspectionQuestionApi.show(inspection.id),
              certificationApi.index(inspection.id),
              certificationApi.getAllSignedCompliancesWithCount(inspection.id),
              commentableApi.index('inspections', inspection.id),
            ]);

            await db.inspectionUploads
              .put({
                inspection_id: inspection.id,
                uploads: inpsectionUploads,
                sync: { synced: true },
              })
              .catch((err) => {
                console.error('Error storing inspection uploads:', err);
              });

            const downloadPromises = [];

            for (const upload of inpsectionUploads) {
              const url = getDownloadUrl(
                upload.upload.GUID,
                null,
                false,
                Date.now(),
                null,
                null,
                false,
              );

              downloadPromises.push(
                downloadAndStoreImage(
                  url,
                  upload.upload.GUID,
                  upload.upload.name,
                  upload.upload.mime_type,
                  response.projects[0].id,
                ),
              );
            }

            await Promise.allSettled(downloadPromises);

            // Process findings
            const findingImagePromises = [];

            for (const finding of findings) {
              finding.sync = {
                synced: true,
                payload: [],
              };

              await db.findings.put(finding).catch((err) => {
                console.error('Error storing finding:', err);
              });

              if (
                finding.finding_uploads &&
                finding.finding_uploads.length > 0
              ) {
                for (const findingUpload of finding.finding_uploads) {
                  const url = getDownloadUrl(
                    findingUpload.upload.GUID,
                    null,
                    false,
                    Date.now(),
                    null,
                    null,
                    false,
                  );

                  findingImagePromises.push(
                    downloadAndStoreImage(
                      url,
                      findingUpload.upload.GUID,
                      findingUpload.upload.name,
                      findingUpload.upload.mime_type,
                      response.projects[0].id,
                    ),
                  );
                }
              }
            }

            // Wait for all finding image downloads to complete
            await Promise.allSettled(findingImagePromises);
            updateProgress(1, steps[4] || 'Downloading inspection data...');

            // Process inspection questions
            const templateId = questionData.template.id;

            // inspectionQuestionApi.show falls back to the cached row when the
            // request fails, so on a re-download `questionData` may BE the local
            // row — including answers the inspector has not uploaded yet.
            // Force-marking that synced would make the sync query (!sync.synced)
            // skip it forever, silently discarding their work.
            const existingQuestions = await db.questions
              .get(Number(inspection.id))
              .catch(() => null);
            const hasPendingAnswers = existingQuestions?.sync?.synced === false;

            questionData.inspection_id = inspection.id;
            questionData.sync = hasPendingAnswers
              ? existingQuestions.sync
              : { synced: true };

            // Only when the inspection is on a different template than the one
            // downloaded above. Otherwise this repeated the whole template
            // metadata block — get, finding types, question config, priority and
            // enforcement options — a second time, ~6s into the download.
            if (Number(templateId) !== Number(inspectionTemplateId)) {
              await Promise.all([
                inspectionTemplateApi.get(templateId),
                fetchInspectionTemplateData(templateId),
              ]);
            }

            // Process condition questions
            if (questionData.template.question_groups) {
              const conditionGroup =
                questionData.template.question_groups.find(
                  (group) => group.name === 'Condition Group',
                );

              if (conditionGroup) {
                const conditionQuestions = conditionGroup.questions.filter(
                  (question) => question.input_label === 'Condition',
                );

                for (const question of conditionQuestions) {
                  // Result was previously discarded here too.
                  const configValues = await questionConfigValueApi
                    .index(question.id)
                    .catch((err) => {
                      console.warn(
                        'Error fetching question config values:',
                        err,
                      );

                      return null;
                    });

                  if (configValues) {
                    await db.questionConfigValues
                      .put({
                        question_id: Number(question.id),
                        data: configValues,
                      })
                      .catch((err) => {
                        console.error(
                          'Error storing question config values:',
                          err,
                        );
                      });
                  }
                }
              }
            }

            // Store question data. Skipped entirely when the local copy holds
            // unsynced answers: a re-download must not overwrite work that has
            // not reached the server, even with a fresh server copy.
            if (hasPendingAnswers) {
              console.warn(
                `Kept local unsynced answers for inspection ${inspection.id}; skipping question overwrite.`,
              );
            } else {
              await db.questions.put(questionData).catch((err) => {
                console.error('Error storing questions:', err);
              });
            }

            // Store certifications
            await db.certifications
              .put({
                ...certifications,
                inspectionId: Number(certifications.inspectionId),
                sync: { synced: true, payload: [] },
              })
              .catch((err) => {
                console.error('Error storing certifications:', err);
              });

            // Store inspection notes
            await db.inspectionNotes
              .put({
                inspection_id: inspection.id,
                notes: notes,
                sync: { synced: true, payload: [] },
              })
              .catch((err) => {
                console.error('Error storing inspection notes:', err);
              });

            updateProgress(1);
          } catch (inspectionError) {
            console.error(
              `Error processing inspection ${inspection.id}:`,
              inspectionError,
            );
            // Continue with next inspection
          }
        }
      } catch (projectInspectionError) {
        console.error(
          `Error fetching inspections for project ${projectId}:`,
          projectInspectionError,
        );
        // Continue with next project
      }
      // }

      // Complete the download process
      setProgress(100);
      setStatusText(steps[steps.length - 1] || 'Download complete!');
      toast.success('Download Complete to current device');
      setOfflineProjectsFunc();

      // Reset download state after a delay
      setTimeout(() => {
        setDownloading(false);
      }, 2000);
    } catch (error) {
      console.error('Download failed:', error);
      setStatusText(`Download failed: ${error.message}`);

      toast.error('Download failed: ' + error.message);

      // Reset download state after a delay
      setTimeout(() => {
        setDownloading(false);
      }, 3000);
    }
  };

  const removeOfflineProjects = async (projectId) => {
    // Awaited end to end: this used to return before either delete ran, so a
    // caller awaiting it could not tell whether the project was really gone.
    let removed = true;

    try {
      await removeOfflineProject(projectId);
      await offlineApi.delete(projectId);
    } catch (e) {
      console.log('error', e);
      toast.error('Error removing project');
      removed = false;
    }

    setOfflineProjectsFunc();

    return removed;
  };

  const syncOfflineProjects = async (projectId) => {
    try {
      setIsSyncing(true);
      setSyncStatus('Preparing to sync project...');
      setSyncProgress(0);

      const { remaining, interrupted } = await syncOfflineProject(projectId, {
        onStatus: setSyncStatus,
        onProgress: setSyncProgress,
      });

      // SW2-2443: the local copy is the only copy of anything not yet uploaded.
      // This used to delete it unconditionally and report success, so a
      // connection dropped mid-sync destroyed the unsent work.
      if (remaining > 0) {
        const items = `${remaining} item${remaining === 1 ? '' : 's'}`;
        // Not interrupted means the server was reached and refused the rest, so
        // "check your connection" would send the inspector round in circles.
        const message = interrupted
          ? `Sync incomplete: ${items} didn't upload. Check your connection and sync again.`
          : `Sync incomplete: ${items} could not be uploaded. Your work is still saved on this device — try again later, or contact support if this keeps happening.`;

        setSyncStatus(message);
        toast.error(message);

        return;
      }

      if (!(await removeOfflineProjects(projectId))) return;

      setSyncProgress(100);
      setSyncStatus('Project synced successfully');
      toast.success('Project synced successfully');
    } catch (e) {
      console.log('error', e);

      toast.error('Error syncing project: ' + e.message);
    } finally {
      setIsSyncing(false);
    }
  };

  const showIcons = useCallback((projectId, allProjects, inspection) => {
    const isOfflineDownloaded = offlineProjects.find(
      (project) => project.id === projectId,
    );

    if (navigator.onLine === false) {
      return <></>;
    }

    const ctaText = getCtaText(inspection.dueDate, inspection.inspectionStatus);

    if (isOfflineDownloaded && !inspection.external_project && (['Start Inspection', 'Past Due', 'Finish'].includes(ctaText))) {
      return (
        <>
          <FontAwesomeIcon
            icon={faTrash}
            color='red'
            cursor='pointer'
            style={{ margin: '5px', }}
            onClick={() => {
              setShowRemoveOfflineDownloadModal(true)
              setOfflineProjectId(projectId)
            }}
          />
          <FontAwesomeIcon
            icon={faUpload}
            color='green'
            cursor='pointer'
            style={{ margin: '5px' }}
            onClick={() => syncOfflineProjects(projectId)}
          />
        </>
      );
    } else if (inspection && !inspection.external_project && (['Start Inspection', 'Past Due', 'Finish'].includes(ctaText))) {
      return (
        <>
          <FontAwesomeIcon
            icon={faDownload}
            color='blue'
            cursor='pointer'
            style={{ margin: '5px' }}
            onClick={() =>
              onDownload(
                projectId,
                inspection.inspection_template_id,
                allProjects,
                inspection.project_inspection_template_id ||
                  inspection.junctionId,
              )
            }
          />
        </>
      );
    }
    else {
      return <>
      </>

    }
  }, [offlineProjects, isOffline]);

  return (
    <>
      <div className='inspections-due' ref={widgetRef}>
        <header
          className='inspections-due__header'
          style={widgetWidth > 700 ? oneColumnHeader : null}
        >
          <h2 className='inspections-due__title'>My Projects</h2>
          <div className='inspections-due__bars'>
            {/* <section style={{ display: isCalendar ? "none" : "flex" }}>{appContext.state.user.name}</section> */}
            <input
              //  style={{ flexBasis: isCalendar ? "100%" : "60%" }}
              onChange={(event) => setSearch(event.target.value)}
              placeholder='Search'
            />
          </div>
          <div className='inspections-due__direction'>
            {selectedProjects.length > 0 && (
              <>
                <button
                  className='primary'
                  onClick={() => setOpenDirectionModal(true)}
                >
                  Directions
                </button>
              </>
            )}
          </div>
          <div className='inspections-due__ellipsis-button'>
            <span onClick={(event) => setAnchorEl(event.currentTarget)}>
              <FontAwesomeIcon icon={faEllipsisV} />
            </span>
          </div>
          <EllipsisPopover
            handleCloseFunc={handleClose}
            id={id}
            open={open}
            anchorEl={anchorEl}
            thirdOption={{
              sx: { p: 1 },
              icon: faList,
              optionName: 'Project List',
              handleOnClickFunc: handleProjectListView,
            }}
            forthOption={{
              sx: { p: 1 },
              icon: viewIcon,
              optionName: viewName,
              handleOnClickFunc: handleChangeView,
            }}
          />
        </header>

        <div
          data-rbd-drag-handle-context-id={providedProps}
          data-rbd-drag-handle-draggable-id='gibberish'
          style={{
            // When you set the data-rbd-drag-handle-context-id, RBD applies cursor: grab, so we need to revert that
            cursor: 'auto',
          }}
        >
          <div className="inspections-due__body">
            <div className="inspections-due__content">

              {localStorage.getItem('myProjectsWidgetPreference') === 'InspectionsDue' ?
                // ------------------------ Inspection Due List View ------------------------------------------------------------------- 
                (<section className="inspection-due__list-view">
                  <div className="inspections-due__table-head">
                    <h3 onClick={sortBySiteName}>Site Name <span><FontAwesomeIcon icon={faSort} /></span></h3>
                    <h3 onClick={sortByClientName}>Client Name <span><FontAwesomeIcon icon={faSort} /></span></h3>
                    <h3 onClick={sortByDueDate}>Due Date <span><FontAwesomeIcon icon={faSort} /></span> </h3>
                    <h3>Action</h3>
                  </div>

                  {!isLoadingForID ?
                    (displayStatsForID && displayStatsForID.map((ele, index) => {
                      const { siteName, clientName, dueDate, projectId, inspectionStatus } = ele;

                      const formattedTodayDate = new Date();
                      const formattedDueDate = dueDate ? parseDueDateLocal(dueDate) : null;

                      let startEvpInspection = false;

                      if (formattedDueDate && formattedTodayDate.setHours(0, 0, 0, 0) === formattedDueDate.getTime() && inspectionStatus === "scheduled") {
                        startEvpInspection = true;
                      }

                      return (
                        <div key={index} className="inspections-due__table-body">
                          {tenantFeatureContext.tenantHasFeature('Enable Route Planner') ?
                            <input
                              type="checkbox"
                              defaultChecked={selectedProjects.find(selectedProject => selectedProject.id === ele.projectId && selectedProject.inspectionId === ele.inspectionId)}
                              onChange={() => handleCheckboxChange({ id: ele.projectId, latitude: ele.latitude, longitude: ele.longitude, inspectionId: ele.inspectionId, name: ele.siteName })}
                              disabled={
                                selectedProjects.find(selectedProject => selectedProject.id === ele.projectId && selectedProject.inspectionId !== ele.inspectionId) ||
                                (selectedProjects.length === 10 && !selectedProjects.find(selectedProject => selectedProject.id === ele.projectId))
                              }
                            /> : <text></text>}
                          <li>{siteName}</li>
                          <li>{clientName}</li>
                          <li>{dueDate}</li>

                          {ele.division &&
                            ele.division.tenant_id === user.tenantId ? (
                            <>
                              <li>
                                {ele.external_project &&
                                  (inspectionStatus == 'new' ||
                                    startEvpInspection) ? (
                                  <span
                                    className={`list-view-submit-btn`}
                                    onClick={() =>
                                      handleEPVInspShowModal(
                                        ele,
                                        startEvpInspection,
                                      )
                                    }
                                  >
                                    Submit
                                  </span>
                                ) : getCtaText(dueDate, inspectionStatus) === 'Start Inspection' ? (
                                  (() => {
                                    const inspKey = `${ele.projectId}_${ele.inspectionId}`;
                                    const isStarting = startingInspectionKey === inspKey;

                                    return (
                                      <span
                                        className={classNames(
                                          handleButtonColors(dueDate, inspectionStatus),
                                          isStarting && 'disabled-btn',
                                        )}
                                        onClick={async () => {
                                          if (isStarting) return;
                                          setStartingInspectionKey(inspKey);
                                          const okToStart = await checkAgreementBeforeStart(ele);

                                          if (!okToStart) {
                                            setStartingInspectionKey(null);

                                            return;
                                          }
                                          if (inspectionStatus === 'new' && ele.inspectionId > 0) {
                                            appDivisionContext.saveAppDivisionId(ele.division.id);
                                            goToInspection(ele.inspectionId);
                                          } else if (ele.junctionId > 0 || ele.project_inspection_template_id > 0) {
                                            handleStartInspection(ele).finally(() => setStartingInspectionKey(null));
                                          } else {
                                            handleCalendarStartInspection(ele).finally(() => setStartingInspectionKey(null));
                                          }
                                        }}
                                        style={{ cursor: isStarting ? 'not-allowed' : 'pointer', opacity: isStarting ? 0.6 : 1 }}
                                      >
                                        {isStarting ? 'Loading...' : (inspectionStatus === 'new' && ele.inspectionId > 0 ? 'Open Inspection' : getCtaText(dueDate, inspectionStatus))}
                                      </span>
                                    );
                                  })()
                                ) : (
                                  <Link
                                    to={getCtaRoute(
                                      inspectionStatus,
                                      dueDate,
                                      projectId,
                                      ele.inspectionId,
                                    )}
                                  >
                                    <span
                                      className={classNames(
                                        handleButtonColors(
                                          dueDate,
                                          inspectionStatus,
                                        ),
                                      )}
                                      onClick={() => appDivisionContext.saveAppDivisionId(ele.division.id)}
                                    >
                                      {getCtaText(dueDate, inspectionStatus)}
                                    </span>
                                  </Link>
                                )}
                                {tenantFeatureContext.tenantHasFeature('Enable Offline Mode') &&
                                  <span style={{ width: '60px' }} >
                                    {showIcons(projectId, displayStatsForID, ele)}
                                  </span>
                                }
                              </li>

                            </>
                          ) : (
                            <>
                              <li>
                                <span
                                  className={classNames(
                                    handleButtonColors(dueDate, inspectionStatus),
                                  )}
                                  onClick={() => handleRedirect(ele)}
                                >
                                  {getCtaText(dueDate, inspectionStatus)}
                                </span>
                                {tenantFeatureContext.tenantHasFeature('Enable Offline Mode') &&
                                  <span style={{ width: '60px' }}>   </span>
                                }
                              </li>

                            </>
                          )}
                        </div>
                      );
                    })
                    ) : (
                      <Loading what='data' />
                    )}
                </section>
                ) : localStorage.getItem('myProjectsWidgetPreference') ===
                  'ProjectList' ? (
                  // ------------------------ Project List View -------------------------------------------------------------------
                  <section className='inspection-due__calendar-view'>
                    <ProjectList
                      setSelectedProjects={setSelectedProjects}
                      selectedProjects={selectedProjects}
                    />
                  </section>
                ) : (
                  // ------------------------ Inspection Due Calendar View -------------------------------------------------------------------
                  <section className='inspection-due__calendar-view'>
                    <CalendarView
                      dueProjects={displayStatsForID}
                      getCtaRoute={getCtaRoute}
                      getCtaText={getCtaText}
                      onStartInspection={(ele) => {
                        if (ele.inspectionStatus === 'new' && ele.inspectionId > 0) {
                          appDivisionContext.saveAppDivisionId(ele.division.id);
                          goToInspection(ele.inspectionId);
                        } else if (ele.junctionId > 0 || ele.project_inspection_template_id > 0) {
                          handleStartInspection(ele);
                        } else {
                          handleCalendarStartInspection(ele);
                        }
                      }}
                    />
                  </section>
                )}
            </div>

            {showExternalProjectInspectionModal ? (
              <ExternalProjectsInspectionSubmitModal
                handleClose={() => setShowExternalProjectInspectionModal(false)}
                handleSubmit={handleEPVInspSubmit}
                modalInspection={modalInspection}
                title='Submit EVP Inspection'
                inspectionTypes={inspectionTypes}
                project_name={modalInspection.siteName}
              />
            ) : (
              <></>
            )}

            <footer className='inspections-due__footer'>
              <span></span>
              <span onClick={changeShowMore}>
                Show {mdSize === 6 ? 'More' : 'Less'}{' '}
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </span>
              {selectedProjects.length ? (
                <span>
                  {' '}
                  Selected {selectedProjects.length} projects{' '}
                  {selectedProjects.length === 10 && '(max)'}{' '}
                </span>
              ) : (
                <span></span>
              )}
            </footer>
          </div>
        </div>
      </div>
      {openDirectionModal && (
        <FormContextProvider>
          <DirectionModal
            Submit={(direction) => GetDirection(direction)}
            onClose={() => setOpenDirectionModal(false)}
          />
        </FormContextProvider>
      )}
      <ConfirmationModal
        show={showRemoveOfflineDownloadModal}
        handleConfirm={() => {
          removeOfflineProjects(offlineProjectId);
          setShowRemoveOfflineDownloadModal(false);
          setOfflineProjectId(null);
          toast.success('Offline download removed successfully');
        }
        }
        handleClose={() => setShowRemoveOfflineDownloadModal(false)}
        title='Are you sure you want to remove offline download?'
        subTitle={`You're about to remove this downloaded project. This will permanently delete all associated work performed offline and cannot be undone. Are you sure you want to continue?`}
        buttonText='Yes, I understand'
        errorMsg={'Error removing offline download.'}
      />

      {downloading && (
        <Paper
          elevation={4}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            padding: 2,
            width: 250,
          }}
        >
          <Typography variant='body2' sx={{ marginBottom: 1 }}>
            {statusText}
          </Typography>
          <LinearProgress
            variant='determinate'
            value={progress}
            sx={{ marginBottom: 1 }}
          />
          <Typography variant='body2'>{progress}% completed</Typography>
        </Paper>
      )}
      {
        isSyncing && (
          <Paper
            elevation={4}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              padding: 2,
              width: 250,
            }}
          >
            <Typography variant='body2' sx={{ marginBottom: 1 }}>
              {syncStatus}
            </Typography>
            <LinearProgress
              variant='determinate'
              value={syncProgress}
              sx={{ marginBottom: 1 }}
            />
            <Typography variant='body2'>{syncProgress}% completed</Typography>
          </Paper>
        )

      }

      {mfaData && (
        <MFAVerificationModal
          show={showMFAModal}
          handleClose={handleMFAModalClose}
          onVerificationSuccess={handleMFAVerificationSuccess}
          email={mfaData.email}
          sendVerificationCode={authStore.sendVerificationCode}
          verifyCode={authStore.verifyCode}
        />
      )}
    </>
  );
};

export default InspectionsDue;
